import React from "react";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputMask } from "primereact/inputmask";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import agent from "../../../agent";

const DAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

const defaultSchedule = () => DAYS.map((_, i) => ({
    day: i + 1,
    weekend: i >= 5,
    start: i < 5 ? "09:00" : "",
    end: i < 5 ? "18:00" : "",
}));

const fromPoint = (point) => {
    if (!point) {
        return {
            kato: null, name: "", enabled: true, minStorageDays: null, maxStorageDays: null,
            streetAbbrId: null, street: "", house: "", office: "",
            schedule: defaultSchedule(), message: "",
        };
    }
    return {
        kato: { kato: point.kato, shortName: point.locality, fullName: point.locality },
        name: point.name || "",
        enabled: point.enabled,
        minStorageDays: point.minStorageDays,
        maxStorageDays: point.maxStorageDays,
        streetAbbrId: point.streetAbbrId ?? null,
        street: point.street || "",
        house: point.house || "",
        office: point.office || "",
        schedule: DAYS.map((_, i) => {
            const day = (point.schedule || []).find((d) => d.day === i + 1);
            // выходной может быть с часами (так заведена суббота у многих
            // точек): часы показываем, но признак не теряем
            return { day: i + 1, weekend: day ? !!day.weekend || !day.start : true, start: day?.start || "", end: day?.end || "" };
        }),
        message: point.message || "",
    };
};

/** Ошибки формы до отправки; остальное проверит граф. */
const validate = (form) => {
    if (!form.kato?.kato) return "Выберите населённый пункт";
    if (!form.street.trim() && (form.streetAbbrId || form.house.trim() || form.office.trim())) return "Укажите улицу";
    const { minStorageDays: min, maxStorageDays: max } = form;
    if (min != null && max != null && min > max) return "Минимальный срок хранения больше максимального";
    for (const d of form.schedule) {
        const start = d.start.replace(/_/g, "");
        const end = d.end.replace(/_/g, "");
        if (!start && !end) continue;
        if (!TIME.test(start) || !TIME.test(end)) return `${DAYS[d.day - 1]}: укажите начало и конец в формате ЧЧ:ММ`;
        if (start >= end) return `${DAYS[d.day - 1]}: начало работы позже конца`;
    }
    return null;
};

/**
 * Точка кросс-докинга: пункт, название и адрес (уходят в адрес заказа),
 * сроки хранения и график работы.
 */
const PointDialog = ({ point, onSaved, onHide }) => {
    const [form, setForm] = React.useState(() => fromPoint(point));
    const [katoOptions, setKatoOptions] = React.useState([]);
    const [streetAbbrs, setStreetAbbrs] = React.useState([]);

    React.useEffect(() => {
        agent.StreetAbbr.all()
            .then((res) => setStreetAbbrs(Array.isArray(res) ? res : []))
            .catch(() => setStreetAbbrs([]));
    }, []);
    const [error, setError] = React.useState(null);
    const [saving, setSaving] = React.useState(false);

    const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    const isEmpty = (time) => !time.replace(/[_:]/g, "");
    // часы, вписанные в пустой выходной, делают его рабочим
    const setDay = (index, key, value) => setForm((prev) => ({
        ...prev,
        schedule: prev.schedule.map((d, i) => {
            if (i !== index) return d;
            const opened = d.weekend && isEmpty(d.start) && isEmpty(d.end) && !isEmpty(value);
            return { ...d, [key]: value, weekend: opened ? false : d.weekend };
        }),
    }));
    const setWeekend = (index, weekend) => setForm((prev) => ({
        ...prev,
        schedule: prev.schedule.map((d, i) => (i === index ? { ...d, weekend } : d)),
    }));
    const dayOff = (index) => setForm((prev) => ({
        ...prev,
        schedule: prev.schedule.map((d, i) => (i === index ? { ...d, weekend: true, start: "", end: "" } : d)),
    }));
    const copyMonday = () => setForm((prev) => ({
        ...prev,
        schedule: prev.schedule.map((d, i) => (i > 0 && i < 5
            ? { ...d, weekend: prev.schedule[0].weekend, start: prev.schedule[0].start, end: prev.schedule[0].end }
            : d)),
    }));

    const searchKato = (event) =>
        agent.Kato.searchGraph(event.query, 0, 20)
            .then((res) => setKatoOptions(Array.isArray(res) ? res : []))
            .catch(() => setKatoOptions([]));

    const save = async () => {
        const problem = validate(form);
        if (problem) {
            setError(problem);
            return;
        }
        const body = {
            kato: form.kato.kato,
            name: form.name,
            enabled: form.enabled,
            minStorageDays: form.minStorageDays,
            maxStorageDays: form.maxStorageDays,
            streetAbbrId: form.streetAbbrId,
            street: form.street,
            house: form.house,
            office: form.office,
            // день без часов граф запишет выходным в любом случае
            schedule: form.schedule.map((d) => {
                const start = d.start.replace(/_/g, "") || null;
                return { day: d.day, weekend: d.weekend || !start, start, end: d.end.replace(/_/g, "") || null };
            }),
            message: form.message,
        };
        setSaving(true);
        setError(null);
        try {
            const saved = point
                ? await agent.CrossDocking.updatePoint(point.id, body)
                : await agent.CrossDocking.createPoint(body);
            onSaved(saved);
        } catch (err) {
            setError(agent.handleError(err));
        } finally {
            setSaving(false);
        }
    };

    const footer = (
        <div>
            <Button label="Отменить" icon="pi pi-times" className="p-button-text" onClick={onHide} />
            <Button label="Сохранить" icon="pi pi-check" loading={saving} onClick={save} />
        </div>
    );

    const katoItem = (item) => (
        <div>
            <div>{item.shortName}</div>
            <small style={{ color: "#6b7280" }}>{item.fullName} · {item.kato}</small>
        </div>
    );

    return (
        <Dialog
            header={point ? `Точка кросс-докинга #${point.id}` : "Новая точка кросс-докинга"}
            visible
            style={{ width: "52rem" }}
            breakpoints={{ "960px": "95vw" }}
            footer={footer}
            onHide={onHide}
        >
            {error && <Message severity="error" text={error} style={{ width: "100%", marginBottom: 12 }} />}

            <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col-12 p-md-6">
                    <label htmlFor="cdp-kato">Населённый пункт</label>
                    <AutoComplete
                        inputId="cdp-kato"
                        value={form.kato}
                        suggestions={katoOptions}
                        completeMethod={searchKato}
                        field="shortName"
                        itemTemplate={katoItem}
                        onChange={(e) => set("kato", e.value)}
                        placeholder="Начните вводить название"
                    />
                    {form.kato?.kato && <small style={{ color: "#6b7280" }}>КАТО {form.kato.kato}</small>}
                </div>
                <div className="p-field p-col-12 p-md-6 p-d-flex p-ai-center" style={{ gap: 6, marginTop: 24 }}>
                    <Checkbox inputId="cdp-enabled" checked={form.enabled} onChange={(e) => set("enabled", e.checked)} />
                    <label htmlFor="cdp-enabled" style={{ margin: 0 }}>Активна</label>
                    <small style={{ color: "#6b7280" }}>— в пункте может быть активна одна точка</small>
                </div>
                <div className="p-field p-col-12">
                    <label htmlFor="cdp-name">Название</label>
                    <InputText id="cdp-name" value={form.name} maxLength={255}
                               onChange={(e) => set("name", e.target.value)} />
                    <small style={{ color: "#6b7280" }}>Уходит в адрес заказа как название компании получателя на точке</small>
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label htmlFor="cdp-street-abbr">Тип улицы</label>
                    <Dropdown inputId="cdp-street-abbr" value={form.streetAbbrId} options={streetAbbrs}
                              optionLabel="nameRu" optionValue="id" filter showClear placeholder="ул., пр-т…"
                              onChange={(e) => set("streetAbbrId", e.value ?? null)} />
                </div>
                <div className="p-field p-col-12 p-md-5">
                    <label htmlFor="cdp-street">Улица</label>
                    <InputText id="cdp-street" value={form.street} maxLength={100}
                               onChange={(e) => set("street", e.target.value)} />
                </div>
                <div className="p-field p-col-6 p-md-2">
                    <label htmlFor="cdp-house">Дом</label>
                    <InputText id="cdp-house" value={form.house} maxLength={10}
                               onChange={(e) => set("house", e.target.value)} />
                </div>
                <div className="p-field p-col-6 p-md-2">
                    <label htmlFor="cdp-office">Офис</label>
                    <InputText id="cdp-office" value={form.office} maxLength={10}
                               onChange={(e) => set("office", e.target.value)} />
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label htmlFor="cdp-min">Хранение, от (дней)</label>
                    <InputNumber inputId="cdp-min" value={form.minStorageDays} min={0} max={365}
                                 onValueChange={(e) => set("minStorageDays", e.value ?? null)} />
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label htmlFor="cdp-max">Хранение, до (дней)</label>
                    <InputNumber inputId="cdp-max" value={form.maxStorageDays} min={0} max={365}
                                 onValueChange={(e) => set("maxStorageDays", e.value ?? null)} />
                </div>
            </div>

            <div className="p-d-flex p-jc-between p-ai-center">
                <h4 style={{ margin: "8px 0" }}>График работы</h4>
                <Button label="Пн → Вт–Пт" icon="pi pi-copy" className="p-button-text p-button-sm" onClick={copyMonday}
                        tooltip="Скопировать часы понедельника на остальные будни" />
            </div>
            <small style={{ color: "#6b7280" }}>
                День без часов — выходной. Часы у выходного можно оставить: в график строкой он не попадёт,
                а время закрытия по нему не считается.
            </small>
            <div className="p-grid p-mt-2">
                {form.schedule.map((d, index) => (
                    <div key={d.day} className="p-col-12 p-md-6 p-d-flex p-ai-center" style={{ gap: 8 }}>
                        <b style={{ width: "2rem", color: d.weekend ? "#b91c1c" : undefined }}>{DAYS[index]}</b>
                        <InputMask mask="99:99" value={d.start} placeholder="ЧЧ:ММ"
                                   style={{ width: "5.5rem", opacity: d.weekend ? 0.6 : 1 }}
                                   onChange={(e) => setDay(index, "start", e.value || "")} />
                        <span>—</span>
                        <InputMask mask="99:99" value={d.end} placeholder="ЧЧ:ММ"
                                   style={{ width: "5.5rem", opacity: d.weekend ? 0.6 : 1 }}
                                   onChange={(e) => setDay(index, "end", e.value || "")} />
                        <Checkbox inputId={`cdp-off-${d.day}`} checked={d.weekend}
                                  onChange={(e) => setWeekend(index, e.checked)} />
                        <label htmlFor={`cdp-off-${d.day}`} style={{ margin: 0 }}>выходной</label>
                        <Button icon="pi pi-times" className="p-button-text p-button-rounded p-button-sm"
                                tooltip="Убрать часы" onClick={() => dayOff(index)} />
                    </div>
                ))}
            </div>

            <div className="p-field p-mt-3">
                <label htmlFor="cdp-message">Комментарий к графику</label>
                <InputTextarea id="cdp-message" value={form.message} rows={2} autoResize maxLength={200}
                               style={{ width: "100%" }} onChange={(e) => set("message", e.target.value)} />
            </div>
        </Dialog>
    );
};

export default PointDialog;
